import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import './Loges.css';

interface Loge {
  image: string;
  orient: string;
  nom: string;
  presentation: string;
  jour: string;
  date: string;
  membresActifs: number;
}

function Loges() {
  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [loges, setLoges] = useState<Loge[]>([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const fetchLoges = async () => {
      try {
        const db = getFirestore();
        const logesSnapshot = await getDocs(collection(db, 'Loges'));
        const membresRef = collection(db, 'Membres');
        
        // ⭐ Récupérer TOUS les membres valides une seule fois
        const allMembresQuery = query(
          membresRef,
          where('statut', '==', 'Valide')
        );
        const allMembresSnapshot = await getDocs(allMembresQuery);
        
        console.log('═══════════════════════════════════════════');
        console.log('📊 DEBUT DEBUG COMPTAGE MEMBRES');
        console.log('═══════════════════════════════════════════');
        
        // ⭐ Normaliser le format des membres (convertir loge → loges)
        const allMembres = allMembresSnapshot.docs.map(doc => {
          const data = doc.data();
          let logesArray: string[] = [];
          
          if (data.loges && Array.isArray(data.loges)) {
            logesArray = data.loges;
          } else if (data.loge) {
            logesArray = [data.loge];
          }
          
          console.log(`Membre: ${data.nom} ${data.prenom}`);
          console.log(`  - loges (array):`, logesArray);
          console.log(`  - Nombre de loges:`, logesArray.length);
          
          return {
            id: doc.id,
            loges: logesArray,
            nom: data.nom,
            prenom: data.prenom,
            ...data
          };
        });

        console.log('\n📋 Total membres valides:', allMembres.length);
        console.log('═══════════════════════════════════════════\n');

        const logesData = logesSnapshot.docs.map((doc) => {
          const logeData = doc.data();
          const logeName = logeData.nom || doc.id;
          
          console.log('🏛️  LOGE:', logeName);
          console.log('   Document ID:', doc.id);
          console.log('   Champ nom:', logeData.nom);
          
          // ⭐ Compter les membres qui appartiennent à cette loge
          const membresForThisLoge = allMembres.filter(membre => {
            const isInLoge = membre.loges && membre.loges.includes(logeName);
            if (isInLoge) {
              console.log(`   ✅ ${membre.nom} ${membre.prenom} appartient à "${logeName}"`);
              console.log(`      Ses loges:`, membre.loges);
            }
            return isInLoge;
          });
          
          const membresActifs = membresForThisLoge.length;

          console.log(`   📊 TOTAL: ${membresActifs} membres actifs`);
          console.log('   ─────────────────────────────────────\n');

          return {
            image: logeData.image || 'Aucune image',
            nom: logeName,
            orient: logeData.orient || '',
            presentation: logeData.presentation || '',
            jour: logeData.jour || '',
            date: logeData.date || '',
            membresActifs: membresActifs
          } as Loge;
        });

        console.log('═══════════════════════════════════════════');
        console.log('✅ FIN DEBUG COMPTAGE MEMBRES');
        console.log('═══════════════════════════════════════════\n');
        
        setLoges(logesData);
      } catch (error) {
        console.error('❌ Erreur lors de la récupération des loges:', error);
      }
    };

    fetchLoges();
  }, []);

  const formatDateTenue = (loge: Loge) => {
    return `${loge.date || ''} ${loge.jour || ''}`.trim() || '-';
  };

  const handleSort = (column: string) => {
    if (column === 'orient' || column === 'nom' || column === 'dateTenue') {
      if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleNavigateToLoge = (logeName: string) => {
    const encodedName = encodeURIComponent(logeName);
    console.log('Navigation vers la loge:', logeName, 'encodé:', encodedName);
    navigate(`/mapage/les-loges/${encodedName}`);
  };

  const exportToExcel = () => {
    const dataToExport = sortedLoges.map(loge => ({
      Image: loge.image === 'Aucune image' ? 'Non' : 'Oui',
      'Nom de la Loge': loge.nom,
      Orient: loge.orient,
      'Date de Tenue': formatDateTenue(loge),
      'Membres Actifs': loge.membresActifs,
      'Présentation': loge.presentation
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Loges");
    XLSX.writeFile(wb, "liste_loges.xlsx");
  };

  const filteredLoges = loges.filter((loge) => {
    if (!searchText) return true;
    const searchRegex = new RegExp(searchText, 'i');
    return searchRegex.test(loge.orient) || 
           searchRegex.test(loge.nom) || 
           searchRegex.test(formatDateTenue(loge));
  });

  const sortedLoges = [...filteredLoges].sort((a, b) => {
    if (sortColumn === 'orient') {
      return sortDirection === 'asc'
        ? (a.orient || '').localeCompare(b.orient || '')
        : (b.orient || '').localeCompare(a.orient || '');
    } else if (sortColumn === 'nom') {
      return sortDirection === 'asc'
        ? a.nom.localeCompare(b.nom)
        : b.nom.localeCompare(a.nom);
    } else if (sortColumn === 'dateTenue') {
      const dateA = formatDateTenue(a);
      const dateB = formatDateTenue(b);
      return sortDirection === 'asc'
        ? dateA.localeCompare(dateB)
        : dateB.localeCompare(dateA);
    }
    return 0;
  });

  return (
    <div className="creer-loges-container">
      <div className="button-container">
        <div className="buttons-group">
          <button
            className="extraire-btn"
            onClick={exportToExcel}
          >
            Extraire
          </button>
          <button
            className="ajouter-btn"
            onClick={() => navigate('/mapage/ajouter-loge')}
          >
            Créer Loge
          </button>
        </div>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par orient, nom ou date"
            value={searchText}
            onChange={handleSearch}
          />
        </div>
      </div>

      <table className="loges-table">
        <thead>
          <tr>
            <th className="image-column">Image</th>
            <th
              onClick={() => handleSort('nom')}
              className="sortable-column"
            >
              Nom {getSortIcon('nom')}
            </th>
            <th
              onClick={() => handleSort('orient')}
              className="sortable-column"
            >
              Orient {getSortIcon('orient')}
            </th>
            <th
              onClick={() => handleSort('dateTenue')}
              className="sortable-column"
            >
              Date de Tenue {getSortIcon('dateTenue')}
            </th>
            <th className="membres-column">Membres Actifs</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedLoges.map((loge, index) => (
            <tr key={loge.nom}>
              <td>
                {loge.image !== 'Aucune image' ? (
                  <img src={loge.image} alt={`Logo ${loge.nom}`} className="loge-image" />
                ) : (
                  'Aucune image'
                )}
              </td>
              <td>{loge.nom}</td>
              <td>{loge.orient}</td>
              <td>{formatDateTenue(loge)}</td>
              <td>{loge.membresActifs}</td>
              <td>
                <div className="actions-container">
                  <button 
                    onClick={() => handleNavigateToLoge(loge.nom)}
                    className="afficher-btn"
                  >
                    Afficher
                  </button>
                  <button 
                    onClick={() => navigate(`/mapage/modifier-loge/${loge.nom}`)}
                    className="modifier-btn"
                  >
                    Modifier
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Loges;